import './IceBreakerCreateButton.pcss';
import { ReactComponent as TopWidgetArrow } from 'assets/top-widget-arrow.svg';
import { ReactComponent as PathIcon } from 'assets/paths-icon.svg';
import classnames from 'classnames';
import { useFormik, yupToFormErrors } from 'formik';
import * as Yup from 'yup';
import React, { useContext } from 'react';
import { useSelector } from 'react-redux';
import { IceBreakerModel } from '@mm/state/model/IceBreakerModel';
import { StoreState } from '@mm/state/store-interfaces';
import { generatePageStructure } from '@mm/state/helpers/filterable-select-helpers';
import { ButtonTagsForm } from '../../../build/components/widgets/buttons/ButtonConfigform/ButtonTagsForm';
import PrimarySelect from '../../../../../common/components/PrimarySelect/PrimarySelect';
import { InputWithEmoji } from '../../../../../common/components/InputWithEmoji/InputWithEmoji';
import { PrimaryButton } from '../../../../../common/buttons/PrimaryButton/PrimaryButton';
import TagModel from '@mm/state/model/TagModel';
import { useButtonHandlers } from '../../../../../common/helpers/ice-breaker-helpers/use-button-handlers';
import { PathGraphContext } from '../../../../../common/widgets/common/PathGraph/PathGraph';
import {
  ButtonType,
  GenericButtonModel,
  PageButtonConfig,
} from '@mm/state/model/ButtonModel';
import { makeButton } from '../../../build/requestModels/widgets/buttonStackWidget/Button';
import FormFieldWrapper from '../../../../../common/components/FormFieldWrapper/FormFieldWrapper';

type Props = {
  iceBreaker: IceBreakerModel;
  closeButtonArea: () => any;
  onCreate: (button: GenericButtonModel<PageButtonConfig>) => any;
};

type IceBreakerCreateButtonFormValues = {
  buttonName: string;
  tags: TagModel[];
  pageId: number;
};

const formFields = {
  buttonName: 'buttonName',
  tags: 'tags',
  pageId: 'pageId',
};

const initialValues = {
  buttonName: '',
  pageId: -1,
  tags: [],
};

const validationSchema = Yup.object().shape({
  buttonName: Yup.string().trim().required('Name Required'),
});

const validate = () => async (values: IceBreakerCreateButtonFormValues) => {
  let errors = {};

  try {
    await validationSchema.validate(values, { abortEarly: false });
  } catch (e) {
    errors = yupToFormErrors(e);
  }

  return errors;
};

export const useDialogueDropdownState = (pageId: number | undefined) => {
  const noOption = {
    name: newDialogueName,
    id: -1,
    pageGroupId: -1,
    kind: null as any,
  };
  const pages = [
    ...useSelector((state: StoreState) => state.page.entities),
    noOption,
  ];
  const selectedPage = pages.find(p => p.id === pageId) || noOption;
  const pageGroups = useSelector(
    (state: StoreState) => state.pageGroup.entities,
  );
  const dialogueDropdownOptions = generatePageStructure(
    pages,
    pageGroups,
    true,
  );

  return {
    dialogueDropdownOptions,
    selectedPage,
  };
};

const newDialogueName = 'Create New Dialogue';

export const useSubmitForm = (
  iceBreaker: IceBreakerModel,
  closeButtonArea: () => void,
  onCreate: (button: GenericButtonModel<PageButtonConfig>) => void,
) => {
  const buttonHandlers = useButtonHandlers();
  const { setActiveNode, deselectWidgetButtons } = useContext(PathGraphContext);

  return async (values: IceBreakerCreateButtonFormValues) => {
    const maxSortOrder = Math.max(
      ...iceBreaker.buttons.map(b => b.sortOrder),
      -1,
    );
    const buttonIndex = maxSortOrder + 1;
    const configExtendObject =
      values.pageId === -1 ? {} : { pageId: values.pageId, pageHidden: true };
    const updatedButton = makeButton({
      type: ButtonType.PAGE,
      name: values.buttonName,
      sortOrder: buttonIndex,
      tags: values.tags,
      configExtendObject,
    }) as GenericButtonModel<PageButtonConfig>;

    deselectWidgetButtons?.(iceBreaker.buttons);

    const newButton = await buttonHandlers.createButton(
      iceBreaker,
      updatedButton,
    );

    if (newButton) {
      setActiveNode?.({
        id: newButton.id,
        type: 'button',
        dialogueId: newButton.config.pageId,
        action: 'open',
      });
      onCreate(newButton);
    }

    closeButtonArea();
  };
};

export const IceBreakerCreateButton = (props: Props) => {
  const { iceBreaker, closeButtonArea, onCreate } = props;

  const formProps = useFormik<IceBreakerCreateButtonFormValues>({
    initialValues,
    validate: validate(),
    onSubmit: values => submitForm(values),
  });

  const submitForm = useSubmitForm(iceBreaker, closeButtonArea, onCreate);

  const { dialogueDropdownOptions, selectedPage } = useDialogueDropdownState(
    Number(formProps.values.pageId),
  );

  const onChangeName = (name: string) => {
    formProps.setFieldValue(formFields.buttonName, name);
  };

  const onTagAdd = (tag: { name: string }) => {
    formProps.setFieldValue(formFields.tags, [...formProps.values.tags, tag]);
  };

  const onTagRemove = (tag: { name: string }) => {
    formProps.setFieldValue(
      formFields.tags,
      formProps.values.tags.filter(e => e.name !== tag.name),
    );
  };

  return (
    <form
      onSubmit={formProps.handleSubmit}
      className="ice-breaker-create-button"
    >
      <div className="ice-breaker-button__input-label">
        <span>Icebreaker Question</span>
      </div>
      <FormFieldWrapper
        className="ice-breaker-create-button__text-wrapper"
        error={formProps.errors.buttonName}
        touched={formProps.touched.buttonName}
      >
        <InputWithEmoji
          name={formFields.buttonName}
          className="ice-breaker-create-button__text-input-with-emoji-picker"
          textInputClassName="ice-breaker-create-button__text-input"
          value={formProps.values.buttonName}
          onChange={onChangeName}
          onBlur={() => formProps.setFieldTouched(formFields.buttonName)}
        />
      </FormFieldWrapper>

      <div className="ice-breaker-create-button__associated-dialogue">
        <div className="ice-breaker-create-button__input-label">
          <PathIcon className="ice-breaker-create-button__path-icon" />
          <span>Respond with the following dialogue</span>
        </div>

        <div className="ice-breaker-create-button__associated-dialogue-input-wrapper">
          <PrimarySelect
            showSearchFilter={true}
            placeholder="Select Dialogue"
            className="ice-breaker-create-button__associated-dialogue-input"
            onChange={dialogue =>
              formProps.setFieldValue(formFields.pageId, dialogue.id)
            }
            organizedOptions={dialogueDropdownOptions}
            value={selectedPage}
          />
        </div>
      </div>

      <div className="ice-breaker-create-button__tags-wrapper">
        <ButtonTagsForm
          title="Add a Tag to contacts who click this button"
          kind="add_tags"
          tags={formProps.values.tags}
          onTagAdd={onTagAdd}
          onTagRemove={onTagRemove}
        />
      </div>

      <div className="ice-breaker-create-button__bottom-area">
        <div className="ice-breaker-create-button__bottom-buttons">
          <span
            className="ice-breaker-create-button__cancel-button"
            onClick={closeButtonArea}
          >
            Cancel
          </span>
          <PrimaryButton
            className={classnames('ice-breaker-create-button__edit-button', {
              'ice-breaker-create-button__edit-button--disabled':
                !formProps.isValid,
            })}
            size="lg"
            disabled={!formProps.isValid}
            onClick={formProps.submitForm}
          >
            Create
          </PrimaryButton>
        </div>
      </div>

      <TopWidgetArrow className="ice-breaker-create-button__bottom-arrow" />
    </form>
  );
};
